import { useEffect, useMemo, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { PagesPageVersionsDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { VersionPage } from './components/VersionPage'
import { VersionsNavigation } from './components/VersionsNavigation'

interface VersionHistoryModalProps {
  isOpen: boolean
  onClose: () => void
  pageId: number
}

export const VersionHistoryModal = ({ isOpen, onClose, pageId }: VersionHistoryModalProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerHeight, setContainerHeight] = useState<number | null>(null)
  const [activeVersionId, setActiveVersionId] = useState<number | 'current'>('current')

  const { data, loading, networkActivity } = useQuery(PagesPageVersionsDocument, {
    variables: {
      pageId,
    },
  })

  useEffect(() => {
    if (containerRef.current?.parentElement?.parentElement) {
      const grandparentHeight = containerRef.current.parentElement.parentElement.clientHeight
      setContainerHeight(grandparentHeight)
    }
  }, [loading, networkActivity])

  const { currentVersionId, history } = useMemo(() => {
    const allVersions = data?.handbookPages.pageVersionsConnection.nodes ?? []
    const currentVersion = allVersions[0]
    const rawHistory = allVersions.slice(1)

    const formattedHistory = rawHistory.map((version) => {
      const employee = version.createdByEmployee
      return {
        id: String(version.id),
        author: {
          firstName: employee?.firstName ?? '',
          lastName: employee?.lastName ?? '',
          src: buildUrl(employee?.avatar?.url),
        },
        timestamp: new Date(version.publishedAt),
        onClick: () => setActiveVersionId(version.id),
      }
    })

    return { currentVersionId: currentVersion?.id, history: formattedHistory }
  }, [data])

  const selectedVersionId = activeVersionId === 'current' ? currentVersionId : activeVersionId

  return (
    <F0Dialog
      isOpen={!!isOpen}
      onClose={onClose}
      position="fullscreen"
      title={i18n.t('pages.page.history_modal.title')}
      disableContentPadding
    >
      <Box width="full" flexGrow flexDirection="row" overflowY="hidden" ref={containerRef}>
        <Box
          flexGrow
          overflowY="hidden"
          style={{
            // TODO: this is a hack because the content is inside an scrollarea and is not possible to set the height to the container height
            height: containerHeight ? `${containerHeight}px` : undefined,
          }}
          border={{
            right: {
              width: 's1',
              style: 'solid',
              color: 'grey300',
            },
          }}
        >
          {selectedVersionId && (
            <VersionPage
              pageId={pageId}
              versionId={selectedVersionId}
              isloadingVersions={loading || networkActivity}
            />
          )}
        </Box>
        <VersionsNavigation
          currentVersionId={currentVersionId}
          versions={history}
          activeVersionId={activeVersionId}
          onCurrentVersionClick={() => setActiveVersionId('current')}
          isLoading={loading || networkActivity}
        />
      </Box>
    </F0Dialog>
  )
}
